/* eslint-disable no-return-assign */

import transform from 'lodash/transform';

// Lightweight, dependency-free replacements for the four validate.js validators
// this module relied on (presence, length, numericality, url). validate.js was
// dropped because its URL validator is vulnerable to ReDoS (no upstream patch);
// the native URL parser used below has no backtracking risk. The rule shape
// (see custom_step_validation_rules.js) is preserved so it stays the single
// source of truth for messages.

const isDefined = value => value !== null && value !== undefined;

const isEmpty = (value) => {
  if (!isDefined(value)) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

// validate.js strips a leading "^" from a message (its opt-out from prefixing
// the humanized attribute name). Preserve that so existing i18n strings render
// identically.
const cleanMessage = message => (
  typeof message === 'string' && message.charAt(0) === '^' ? message.slice(1) : message
);

const validatePresence = (value, options) => (
  isEmpty(value) ? [options.message || "can't be blank"] : []
);

const validateLength = (value, options) => {
  if (!isDefined(value)) return [];

  const { length } = String(value);
  if (typeof options.minimum === 'number' && length < options.minimum) {
    return [options.tooShort || `is too short (minimum is ${options.minimum} characters)`];
  }
  if (typeof options.maximum === 'number' && length > options.maximum) {
    return [options.tooLong || `is too long (maximum is ${options.maximum} characters)`];
  }
  return [];
};

const validateNumericality = (value, options) => {
  if (!isDefined(value)) return [];

  const number = typeof value === 'string' ? +value : value;
  if (typeof number !== 'number' || Number.isNaN(number)) {
    return [options.notValid || 'is not a number'];
  }
  if (options.onlyInteger && !Number.isInteger(number)) {
    return [options.notInteger || 'must be an integer'];
  }
  if (typeof options.greaterThanOrEqualTo === 'number' && !(number >= options.greaterThanOrEqualTo)) {
    return [options.notGreaterThanOrEqualTo || `must be greater than or equal to ${options.greaterThanOrEqualTo}`];
  }
  if (typeof options.lessThan === 'number' && !(number < options.lessThan)) {
    return [options.notLessThan || `must be less than ${options.lessThan}`];
  }
  return [];
};

const validateUrl = (value, options) => {
  if (!isDefined(value)) return [];

  try {
    const url = new URL(String(value));
    if (!['http:', 'https:'].includes(url.protocol) || !url.hostname) {
      return [options.message || 'is not a valid url'];
    }
  } catch (e) {
    return [options.message || 'is not a valid url'];
  }
  return [];
};

const VALIDATORS = {
  presence: validatePresence,
  length: validateLength,
  numericality: validateNumericality,
  url: validateUrl,
};

// Runs every validator declared for a single field and concatenates their
// messages, mirroring validate.js's per-attribute behavior.
const validateField = (value, rule) => Object.keys(rule).reduce((messages, validatorName) => {
  const validator = VALIDATORS[validatorName];
  if (!validator) return messages;
  return messages.concat(validator(value, rule[validatorName]).map(cleanMessage));
}, []);

export const createValidationMessages = (state, rules) => {
  /*
    Returns a validationMessages object for given state and validations rules.
    validationMessages object returned which can be filtered by step type along with field names
    Eg: {
      "custom_step_link": {"estimatedHours": [], "estimatedMinutes": [], "title": [], "url": []},
      "custom_step_task": {"description": [], "estimatedHours": [], "estimatedMinutes": [],
      "title": []}}
  */

  /* eslint-disable no-param-reassign */
  const validationMessages = transform(rules, (memo, rulesInner, stepType) => memo[stepType] =
    /* eslint-disable no-param-reassign */
    transform(rulesInner, (innerMemo, rule, fieldName) => {
      const value = state[fieldName] === '' ? null : state[fieldName];
      innerMemo[fieldName] = validateField(value, rule);
      return innerMemo;
    }, {}), {});

  return validationMessages;
};

export const isFormInvalid = (state, objName, rules) => {
  const validationMsg = createValidationMessages(state, rules)[objName];
  const msgArray = Object.values(validationMsg)
    .reduce((prev, curr) => prev.concat(curr));

  return msgArray.length > 0;
};
